import { useCallback } from "react";
import { atom, selector, atomFamily, selectorFamily, useRecoilRefresher_UNSTABLE, useSetRecoilState } from "recoil";
import { useRecoilValue } from "recoil";
import axios from "axios";
import { getCustomer } from "../Authorization";
import { COMMUNICATE_ERROR_MESSAGE_GET_AIRPORT, COMMUNICATE_ERROR_MESSAGE_GET_AIRPORT_GROUP, COMMUNICATE_ERROR_MESSAGE_GET_CANDIDATE_AIRPORT, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD } from "../constants/ErrorMessage";

/* 空港グループ一覧state */
const airportGroupListState = atom({
  key: 'airportGroupListState',
  default: selector({
    key: 'airportGroupListStateSelector',
    get: async () => {
      const customer = getCustomer();
      const apiUrl = process.env.REACT_APP_METARTAF_API_URL;
      const apiKey = process.env.REACT_APP_METARTAF_API_KEY;
      const config = {
        method: 'get',
        url: `${apiUrl}/get_airport_group?user=${customer}`,
        headers: { 
          'authorizationToken': apiKey
        },
        errorHandle: {
          message: COMMUNICATE_ERROR_MESSAGE_GET_AIRPORT_GROUP,
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
        }
      };
      
      const data = axios(config)
        .then(response => {
          // console.log(JSON.stringify(response.data));
          const list = response.data[customer] || [];
          return list.sort();
        })
        .catch(e => {
          console.log('Failed to retrieve airport group list.', e);
          return [];
        });
      return data;
    }
  })
});

/* 空港一覧 */
const airportFamily = atomFamily({
  key: 'airportFamily',
  default: selectorFamily({
    key: 'airportFamilySelector',
    get: (airportGroupName) => async () => {
      if (!airportGroupName) {
        return new Promise((resolve) => {
          resolve([]);
        });
      }
      const customer = getCustomer();
      const apiUrl = process.env.REACT_APP_METARTAF_API_URL;
      const apiKey = process.env.REACT_APP_METARTAF_API_KEY;
      const config = {
        method: 'get',
        url: `${apiUrl}/get_airport?user=${customer}&group=${airportGroupName}`,
        headers: { 
          'authorizationToken': apiKey
        },
        errorHandle: {
          message: COMMUNICATE_ERROR_MESSAGE_GET_AIRPORT,
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
        }
      };

      const data = axios(config)
        .then(response => {
          // console.log(JSON.stringify(response.data));
          return response.data[airportGroupName];
        })
        .catch(e => {
          console.log('Failed to retrieve airport list.', e);
          return [];
        });
      return data;
    }
  })
});

/* 空港一覧state */
const airportListState = selectorFamily({
  key: 'airportListState',
  get: (airportGroupName) => ({get}) => {
    return get(airportFamily(airportGroupName));
  },
  set: (airportGroupName) => ({set}, newData) => {
    //newAirportGroupNameが設定されている場合、newAirportGroupNameのstateにsetする
    //設定されていない場合はairportGroupNameにsetする
    const targetName = newData.newAirportGroupName ? newData.newAirportGroupName : airportGroupName;
    set(airportFamily(targetName), newData.newAirportList);
  }
});

/* 候補空港一覧state */
const candidateAirportListState = atom({
  key: 'candidateAirportListState',
  default: selector({
    key: 'candidateAirportListStateSelector',
    get: async () => {
      const apiUrl = process.env.REACT_APP_METARTAF_API_URL;
      const apiKey = process.env.REACT_APP_METARTAF_API_KEY;
      //TODO 顧客毎に持つ候補空港をパラメータに指定(現状はその情報を持ってないのでハードコーディング)
      const candidateAirportIcao = ['RJ', 'RO'];
      const config = {
        method: 'get',
        url: `${apiUrl}/get_candidate_airport?icao=${candidateAirportIcao.join(',')}`,
        headers: { 
          'authorizationToken': apiKey
        },
        errorHandle: {
          message: COMMUNICATE_ERROR_MESSAGE_GET_CANDIDATE_AIRPORT,
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
        }
      };

      const data = axios(config)
        .then(response => {
          // console.log(JSON.stringify(response.data));
          return response.data;
        })
        .catch(e => {
          console.log('Failed to retrieve candidate airport list.', e);
          return [];
        });
      return data;
    }
  })
});

/* 最終更新情報state */
export const lastUpdateState = atom({
  key: 'lastUpdateAirportGroupState',
  default: {
    target: null,
    time: null
  }
});

export const useAirportGroupList = () => {
  const airportGroupList = useRecoilValue(airportGroupListState);

  const refresher = useRecoilRefresher_UNSTABLE(airportGroupListState);

  //空港グループ削除
  const deleteAirportGroup = useCallback(async (targetList) => {
    //空港グループ削除API呼び出し
    const deleted = await _deleteAirportGroup(targetList);

    if (deleted.length > 0) {
      refresher();
    }

    //削除失敗した空港グループの配列を返却
    return targetList.filter(ag => !deleted.includes(ag));

  }, [refresher]);

  return {
    airportGroupList,
    deleteAirportGroup,
    refreshAirportGroupList: refresher,
  }
};

export const useAirportGroup = (airportGroupName) => {
  const airportGroupList = useRecoilValue(airportGroupListState);
  const airportList = useRecoilValue(airportListState(airportGroupName));
  const setLastUpdate = useSetRecoilState(lastUpdateState);

  const refreshList = useRecoilRefresher_UNSTABLE(airportGroupListState);
  const refreshAirportGroup = useRecoilRefresher_UNSTABLE(airportListState(airportGroupName));
  const refresherAll = useCallback(() => {
    refreshList();
    refreshAirportGroup();
  }, [refreshList, refreshAirportGroup]);

  //空港グループ追加
  const insertAirportGroup = useCallback(async (newAirportGroupName, targetList) => {
    //空港グループ登録(更新)API呼び出し
    const result = await _upsertAirportGroup(newAirportGroupName, targetList);
    if (result) {
      refresherAll();
    }
    return result;
  }, [refresherAll]);

  //空港グループ更新
  const updateAirportGroup = useCallback(async (targetList) => {
    //空港グループ登録(更新)API呼び出し
    const result = await _upsertAirportGroup(airportGroupName, targetList);
    if (result) {
      //最終更新情報を更新
      setLastUpdate({target: airportGroupName, time: new Date().getTime()});

      refreshAirportGroup();
    }
    return result;
  }, [airportGroupName, setLastUpdate, refreshAirportGroup]);

  return {
    airportGroupList,
    airportList,
    insertAirportGroup,
    updateAirportGroup,
    refreshAirportGroup,
  }
};

export const useCandidateAirportList = () => {
  const candidateAirportList = useRecoilValue(candidateAirportListState);

  return {
    candidateAirportList,
  }
};

//空港グループ削除API実行
const _deleteAirportGroup = (targetList) => {

  const p = new Promise((resolve) => {

    const customer = getCustomer();
    const apiUrl = process.env.REACT_APP_METARTAF_API_URL;
    const apiKey = process.env.REACT_APP_METARTAF_API_KEY;
  
    const promises = [];
    targetList.forEach(target => {
      const config = {
        method: 'post',
        url: `${apiUrl}/delete_airport_group?user=${customer}&group=${target}`,
        headers: { 
          'authorizationToken': apiKey
        },
      };
  
      promises.push(
        axios(config)
          .then(response => {
            // console.log(JSON.stringify(response.data));
            return target;
          })
          .catch(e => {
            console.log(`Failed to delete airport group(${target}).`, e);
            return null;
          })
      );
    });
  
    Promise.all(promises).then(data => { 
      // console.log(data);
      resolve(data.filter(v => v));
    });
  });

  return p;
};

//空港グループ設定API実行
const _upsertAirportGroup = async (airportGroupName, targetList) => {

  const customer = getCustomer();
  const apiUrl = process.env.REACT_APP_METARTAF_API_URL;
  const apiKey = process.env.REACT_APP_METARTAF_API_KEY;

  const config = {
    method: 'post',
    url: `${apiUrl}/add_airport?user=${customer}&group=${airportGroupName}`,
    headers: { 
      'authorizationToken': apiKey
    },
    data: targetList.join(','),
  };

  const data = axios(config)
    .then(response => {
      // console.log(JSON.stringify(response.data));
      return true;
    })
    .catch(e => {
      console.log(`Failed to set airport group(${airportGroupName}:${targetList.join(',')}).`, e);
      return false;
    });

  return data;
};
